/** @format */

import React, { FunctionComponent, ReactElement, useRef, useState } from 'react';
import CardBody from '../../../../components/Card/CardBody';
import I18n from '../../../../components/Translate/I18n';
import GridContainer from '../../../../components/Grid/GridContainer';
import GridItem from '../../../../components/Grid/GridItem';
import { Card } from '@material-ui/core';
import CustomInputExt from '../../../../components/CustomInput/CustomInputExt';
import {
    SimCardServiceTypeAllowedParamMap,
    SimCardServiceTypeAllowedParamMapAllowedValue,
    SimCardServiceTypeSingleParam,
} from '../../../../interfaces/collections/SimCardServiceInterface';
import { BoolTranslateHelper, BoolTranslateTrueFalseHelper } from '../../../../helpers/BoolTranslateHelper';
import { SdgBmeFormControlCheckBox } from './OplSimCardServiceTypeParamItemSdgBmeFormItem';
import { OplSimCardServiceTypeParamItemSdgViewServicePDP5 } from './OplSimCardServiceTypeParamItemSdgViewServicePDP5';

const _ = require('underscore');

type OplSimCardServiceTypeParamItemSdgServicePDP5FormItemViewType = {
    initialParam: SimCardServiceTypeSingleParam;
};

export const OplSimCardServiceTypeParamItemSdgServicePDP5FormItemView: FunctionComponent<OplSimCardServiceTypeParamItemSdgServicePDP5FormItemViewType> = ({
    initialParam,
}): React.ReactElement => {
    const [raised, setRaised] = useState(false);
    const componentParam = useRef<SimCardServiceTypeSingleParam>(initialParam);
    const [defaultValue, setDefaultValue] = useState<string | number | boolean | Array<string> | Array<number> | Array<boolean> | null>(
        componentParam.current.params.default
    );
    const [allowedValue, setAllowedValue] = useState<Array<SimCardServiceTypeAllowedParamMapAllowedValue>>(
        componentParam.current.params.allowedValue ?? []
    );
    const [globalAllowedValue, setGlobalAllowedValue] = useState<Array<SimCardServiceTypeAllowedParamMapAllowedValue>>(
        componentParam.current.globalParams.allowedValue ?? []
    );

    const defaultSelectRadioValues = (defaultValues: SimCardServiceTypeAllowedParamMap): React.ReactElement => {
        if (!defaultValues.input && defaultValues.type === 'string') {
            return (
                <React.Fragment>
                    <GridItem xs={6}>
                        <I18n trans="prov:feature.default" />
                    </GridItem>
                    <GridItem xs={6}>
                        <React.Fragment>
                            {typeof defaultValue === 'boolean' && <I18n trans={BoolTranslateTrueFalseHelper(defaultValue)} />}
                            {typeof defaultValue !== 'boolean' && defaultValue}
                            {typeof defaultValue !== 'boolean' && !defaultValue && <I18n trans="prov:feature.empty_value" />}
                        </React.Fragment>
                    </GridItem>
                </React.Fragment>
            );
        }
        if (!defaultValues.input && defaultValues.type === 'number') {
            return (
                <GridItem xs={6}>
                    <CustomInputExt required={false} labelText="prov:feature.default" name="default" value={defaultValue} />
                </GridItem>
            );
        }
        if (defaultValues.input === 'select' || defaultValues.input === 'bool') {
            return (
                <React.Fragment key={'form_control_default_select_radio_name' + componentParam.current.paramName + '_parameters'}>
                    <GridItem xs={6}>
                        <I18n trans="prov:feature.default" />
                    </GridItem>
                    <GridItem xs={6}>
                        {defaultValues.allowedValue?.map((item) => {
                            if (typeof item.value === 'boolean' && defaultValue === item.value) {
                                return <I18n trans={BoolTranslateTrueFalseHelper(item.value)} />;
                            } else if (defaultValue === item.value) {
                                return item.value;
                            }
                        })}
                    </GridItem>
                </React.Fragment>
            );
        }
        return <div />;
    };

    const allowedValues = (): React.ReactElement => {
        let element = globalAllowedValue.map((item) => {
            if (item.value === null) {
                return null;
            }
            if (typeof item.value === 'boolean') {
                return (
                    <SdgBmeFormControlCheckBox
                        key={'form_control_check_box' + BoolTranslateTrueFalseHelper(item.value)}
                        value={item.value ? 'true' : 'false'}
                        label={BoolTranslateTrueFalseHelper(item.value)}
                        selectedElement={allowedValue}
                        updateAllowedValue={(): void => {}}
                        type="bool"
                        readonly={true}
                    />
                );
            }
            return (
                <SdgBmeFormControlCheckBox
                    key={'form_control_check_box' + item.value.toString()}
                    value={item.value.toString()}
                    label={item.value.toString()}
                    selectedElement={allowedValue}
                    updateAllowedValue={(): void => {}}
                    type={typeof item.value}
                    readonly={true}
                />
            );
        });
        return (
            <React.Fragment>
                <GridItem xs={6}>
                    <I18n trans="prov:feature.allowed_value" />
                </GridItem>
                <GridItem xs={6}>{element}</GridItem>
            </React.Fragment>
        );
    };

    const apnName = (): ReactElement => {
        if (componentParam.current.params.allowedValue === null || componentParam.current.params.param_name !== 'APN_NAME') {
            return <></>;
        }
        let element = componentParam.current.params.allowedValue.map((item) => {
            return (
                <GridItem key={'apn_name_' + item.value} xs={12}>
                    {item.value}
                </GridItem>
            );
        });
        return (
            <React.Fragment>
                <GridItem xs={6}>
                    <I18n trans="prov:feature.allowed_value" />
                </GridItem>
                <GridItem xs={6}>
                    <GridContainer>{element}</GridContainer>
                </GridItem>
            </React.Fragment>
        );
    };

    return (
        <Card style={{ marginBottom: '10px' }} raised={raised} onMouseOver={(): void => setRaised(true)} onMouseOut={(): void => setRaised(false)}>
            <CardBody>
                <I18n trans="prov:feature.param_name" valueObject={{ param_name: componentParam.current.paramName }} />
                <GridContainer>
                    <GridItem xs={6}>
                        <I18n trans="prov:feature.required" />
                    </GridItem>
                    <GridItem xs={6}>
                        <I18n trans={BoolTranslateHelper(componentParam.current.params.required)} />
                    </GridItem>
                    {allowedValue && componentParam.current.paramName !== 'APN_NAME' && defaultSelectRadioValues(componentParam.current.params)}
                    {globalAllowedValue.length > 0 && componentParam.current.paramName !== 'APN_NAME' && allowedValues()}
                    {globalAllowedValue.length > 0 && componentParam.current.paramName === 'APN_NAME' && apnName()}
                </GridContainer>
            </CardBody>
        </Card>
    );
};
